import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  StyleSheet,
  View,
  Platform,
  Animated,
  Text,
} from 'react-native';
import { Tabs, useRouter } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { StatusBar } from 'expo-status-bar';
import AsyncStorage from '@react-native-async-storage/async-storage';
import NetInfo, { NetInfoState } from '@react-native-community/netinfo';
import { useTranslation } from 'react-i18next';

// CustomTabBar a été extrait dans components/CustomTabBar.tsx pour être
// réutilisé tel quel par la sous-navbar Market (même dégradé violet/or,
// même comportement d'ombre/animation), au lieu d'être dupliqué.
import CustomTabBar, { type TabBarButtonConfig, TabBarVisibilityContext } from '@/components/CustomTabBar';
// TabBarVisibilityContext vit maintenant dans components/CustomTabBar.tsx
// (au lieu d'un fichier séparé contexts/TabBarVisibilityContext.tsx, qui
// n'était pas résolu par Metro en environnement Vercel). Les consommateurs
// (ex: market/_layout.tsx, messages.tsx) doivent importer useTabBarVisibility
// depuis '@/components/CustomTabBar' également.

// ── Hauteur du bandeau offline ───────────────────────────────────────────
const OFFLINE_BANNER_HEIGHT = 36;

// Ajout de 'market' : nouvel onglet menant à la section Market (qui a
// elle-même son propre sous-layout avec 4 boutons, voir
// app/(tabs)/market/_layout.tsx).
type TabKey = 'actu' | 'games-screen' | 'market' | 'messages' | 'friends' | 'profile';

const TAB_CONFIG: TabBarButtonConfig[] = [
  { key: 'actu',         icon: 'home-outline',              iconActive: 'home' },
  { key: 'games-screen', icon: 'game-controller-outline',   iconActive: 'game-controller' },
  { key: 'market',       icon: 'storefront-outline',        iconActive: 'storefront' },
  { key: 'messages',     icon: 'chatbubbles-outline',       iconActive: 'chatbubbles' },
  { key: 'friends',      icon: 'people-outline',            iconActive: 'people' },
  { key: 'profile',      icon: 'person-outline',            iconActive: 'person' },
];

export default function TabsLayout() {
  const router = useRouter();
  const { t } = useTranslation();

  // ── Vérification de session ─────────────────────────────────────────
  // Reprend exactement la logique qui était dans l'ancien home.tsx :
  // on lit la session en cache, et si elle est absente/invalide on
  // redirige vers /login. Tant que la vérification tourne, on affiche
  // un écran de chargement (les <Tabs> ne sont montés qu'après).
  const [sessionChecked, setSessionChecked] = useState(false);

  // Tokens en mémoire, au cas où des écrans enfants en ont besoin
  // via un futur contexte d'auth (pour l'instant juste conservés ici,
  // comme dans l'ancien home.tsx).
  const accessTokenRef  = useRef<string>('');
  const refreshTokenRef = useRef<string>('');
  const expiresAtRef    = useRef<number>(0);

  useEffect(() => {
    const initSession = async () => {
      try {
        const raw = await AsyncStorage.getItem('harmonia_session');

        if (!raw) { router.replace('/login'); return; }

        const session = JSON.parse(raw);

        if (!session?.access_token) { router.replace('/login'); return; }

        accessTokenRef.current  = session.access_token  || '';
        refreshTokenRef.current = session.refresh_token || '';
        expiresAtRef.current    = session.expires_at    || 0;

        setSessionChecked(true);
      } catch (error) {
        console.error('Auth check error:', error);
        router.replace('/login');
      }
    };

    initSession();
  }, [router]);

  const [isOnline, setIsOnline] = useState(true);
  const [isTabBarHidden, setIsTabBarHidden] = useState(false);
  // Ne devient true qu'APRÈS la fin de l'animation de translation — c'est
  // ce qui déclenche le démontage physique du conteneur d'ombre (voir
  // CustomTabBar), pour éviter le résidu semi-transparent laissé par
  // `elevation` (Android) qui ignore parfois `opacity: 0`.
  const [isTabBarFullyHidden, setIsTabBarFullyHidden] = useState(false);

  const offlineBannerAnim = useRef(new Animated.Value(0)).current; // 0 caché, 1 visible
  const tabBarTranslateY  = useRef(new Animated.Value(0)).current;

  // Hauteur réelle de la tab bar, mesurée via onLayout sur CustomTabBar.
  // Avant ce fix, on utilisait une valeur fixe (100) pour la translation,
  // ce qui ne correspondait pas à la vraie hauteur (padding + icônes +
  // bande "hors ligne" éventuelle) selon la plateforme — la tab bar ne
  // sortait donc jamais complètement de l'écran et laissait une bande
  // floutée visible en bas.
  const [measuredTabBarHeight, setMeasuredTabBarHeight] = useState(100);
  const measuredTabBarHeightRef = useRef(100);

  // ── Réseau ────────────────────────────────────────────────────────────
  const updateOnlineStatus = useCallback((online: boolean) => {
    setIsOnline(online);
    Animated.timing(offlineBannerAnim, {
      toValue:         online ? 0 : 1,
      duration:        350,
      useNativeDriver: false, // height/layout → false obligatoire
    }).start();
  }, [offlineBannerAnim]);

  React.useEffect(() => {
    if (Platform.OS === 'web') {
      const handleOnline  = () => updateOnlineStatus(true);
      const handleOffline = () => updateOnlineStatus(false);
      window.addEventListener('online',  handleOnline);
      window.addEventListener('offline', handleOffline);
      updateOnlineStatus(navigator.onLine);
      return () => {
        window.removeEventListener('online',  handleOnline);
        window.removeEventListener('offline', handleOffline);
      };
    } else {
      const unsubscribe = NetInfo.addEventListener((state: NetInfoState) => {
        const connected = state.isConnected === true && state.isInternetReachable !== false;
        updateOnlineStatus(connected);
      });
      NetInfo.fetch().then((state: NetInfoState) => {
        const connected = state.isConnected === true && state.isInternetReachable !== false;
        updateOnlineStatus(connected);
      });
      return () => unsubscribe();
    }
  }, [updateOnlineStatus]);

  // Opacité de l'ombre statique (bottomTabsShadowWrap). Le conteneur
  // d'ombre reste toujours monté (pour ne jamais recalculer son layout),
  // mais son opacité doit tomber à 0 exactement quand le contenu glisse
  // hors écran — sinon l'ombre semi-transparente reste visible et floute
  // la zone même quand la tab bar elle-même a disparu.
  const tabBarShadowOpacity = useRef(new Animated.Value(1)).current;

  // ── Tab bar hide/show (mode chat + scroll) ───────────────────────────
  // Accepte un callback optionnel onDone, appelé une fois l'animation
  // RÉELLEMENT terminée (pas au moment de l'appel) — utilisé par
  // market/_layout.tsx pour séquencer son affichage après confirmation.
  //
  // FIX : la garde d'idempotence précédente ("si l'état demandé est déjà
  // isTabBarHidden, ignorer l'appel") a été RETIRÉE. Elle avait été
  // ajoutée uniquement pour un cas d'usage Market, mais elle bloquait
  // silencieusement TOUS les appelants (actu.tsx, friends.tsx,
  // games-screen.tsx, messages.tsx, profile.tsx) dès que isTabBarHidden
  // se retrouvait désynchronisé de la réalité visuelle — cassant le
  // scroll auto-hide et le double-tap sur tous les écrans, pas
  // seulement en lien avec Market. Retour au comportement original :
  // chaque appel relance systématiquement l'animation, sans condition.
  const setTabBarHidden = useCallback((hidden: boolean, onDone?: () => void) => {
    setIsTabBarHidden(hidden);

    // Si on RÉAFFICHE la barre : remonter immédiatement le wrapper
    // d'ombre (avant même que l'animation ne parte), sinon on verrait
    // la barre glisser vers le haut sans aucune ombre pendant le trajet.
    if (!hidden) setIsTabBarFullyHidden(false);

    Animated.parallel([
      Animated.timing(tabBarTranslateY, {
        // + 20px de marge de sécurité, comme pour le header dans actu.tsx,
        // pour garantir que la tab bar sorte COMPLÈTEMENT de l'écran et ne
        // laisse plus aucune bande visible/floutée en bas.
        toValue: hidden ? measuredTabBarHeightRef.current + 20 : 0,
        duration: 300,
        useNativeDriver: true,
      }),
      Animated.timing(tabBarShadowOpacity, {
        toValue: hidden ? 0 : 1,
        duration: 300,
        useNativeDriver: true,
      }),
    ]).start(({ finished }) => {
      // Si on CACHE la barre : ne démonter le conteneur d'ombre qu'une
      // fois l'animation terminée et la barre réellement hors écran.
      if (finished && hidden) setIsTabBarFullyHidden(true);
      // onDone signale la fin RÉELLE de l'animation (finished === true),
      // pas juste le déclenchement. C'est ce que market/_layout.tsx
      // attend avant d'afficher sa propre navbar.
      if (finished) onDone?.();
    });
  }, [tabBarTranslateY, tabBarShadowOpacity]);

  const bannerHeight = offlineBannerAnim.interpolate({
    inputRange:  [0, 1],
    outputRange: [0, OFFLINE_BANNER_HEIGHT],
  });
  const bannerOpacity = offlineBannerAnim.interpolate({
    inputRange:  [0, 0.5, 1],
    outputRange: [0, 0,   1],
  });

  // ── Écran de chargement pendant la vérification de session ──────────
  // On évite de monter les <Tabs> (et donc les écrans) avant d'être
  // sûr que l'utilisateur est bien authentifié.
  if (!sessionChecked) {
    return (
      <View style={styles.loadingContainer}>
        <Ionicons name="sync" size={40} color="#8A2BE2" />
      </View>
    );
  }

  return (
    <TabBarVisibilityContext.Provider value={{ setTabBarHidden }}>
      <View style={styles.container}>
        <StatusBar style="light" />

        {/* ── Bandeau OFFLINE ── */}
        <Animated.View
          style={[styles.offlineBanner, { height: bannerHeight, opacity: bannerOpacity }]}
          pointerEvents={isOnline ? 'none' : 'auto'}
        >
          <Ionicons name="wifi" size={14} color="#fff" style={styles.offlineIcon} />
          <Text style={styles.offlineText}>{t('tabsLayout.offlineText')}</Text>
        </Animated.View>

        {/* ── Tabs Expo Router — écrans gérés nativement, restent montés ── */}
        <Tabs
          tabBar={(props) => (
            <CustomTabBar
              {...props}
              isOnline={isOnline}
              tabBarTranslateY={tabBarTranslateY}
              tabBarShadowOpacity={tabBarShadowOpacity}
              isTabBarFullyHidden={isTabBarFullyHidden}
              buttonsConfig={TAB_CONFIG}
              onMeasureHeight={(h: number) => {
                if (h > 0 && Math.abs(h - measuredTabBarHeightRef.current) > 1) {
                  measuredTabBarHeightRef.current = h;
                  setMeasuredTabBarHeight(h);
                }
              }}
            />
          )}
          screenOptions={{ headerShown: false }}
        >
          <Tabs.Screen name="actu"          options={{ title: t('tabsLayout.tabs.actu') }} />
          <Tabs.Screen name="games-screen"  options={{ title: t('tabsLayout.tabs.games') }} />
          <Tabs.Screen name="market"        options={{ title: t('tabsLayout.tabs.market') }} />
          <Tabs.Screen name="messages"      options={{ title: t('tabsLayout.tabs.messages') }} />
          <Tabs.Screen name="friends"       options={{ title: t('tabsLayout.tabs.friends') }} />
          <Tabs.Screen name="profile"       options={{ title: t('tabsLayout.tabs.profile') }} />
        </Tabs>
      </View>
    </TabBarVisibilityContext.Provider>
  );
}

// ─── STYLES ───────────────────────────────────────────────────────────────
// Note : les styles propres à la tab bar elle-même (bottomTabsShadowWrap,
// bottomTabsContainer, navOfflineStrip, tabsGradient, tabs, tab,
// activeIndicator, etc.) ont migré avec CustomTabBar dans
// components/CustomTabBar.tsx. Seuls les styles propres à CE layout
// (conteneur global, écran de chargement, bandeau offline) restent ici.
const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F5F5F5',
    ...Platform.select({
      web: {
        height:   '100vh' as any,
        overflow: 'hidden' as any,
      },
    }),
  },

  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#F5F5F5',
  },

  offlineBanner: {
    backgroundColor: '#D32F2F',
    flexDirection:   'row',
    alignItems:      'center',
    justifyContent:  'center',
    overflow:        'hidden',
    zIndex:          100,
    width:           '100%' as any,
  },
  offlineIcon: { marginRight: 6, opacity: 0.9 },
  offlineText: { color: '#fff', fontSize: 13, fontWeight: '600', letterSpacing: 0.3 },
});
